import { useUserSubscription } from './customersSubscriptionData';
import {
    AnnualReport,
    ConnectPlus,
    LiveSessionView,
    LiveTrack,
    Messaging,
    ProfileImageFrame,
    SocialImprovements,
    Trails,
    AdvancedReporting,
    customerSubscriptions,
    premiumFeatures,
    purchasable,
    purchasableSubscriptions,
    subscription,
    subscriptions
} from './subscriptionFields';

/**
 * @typedef {'LiveSessionView' | 'Messaging' | 'LiveTrack' | 'Trails' | 'ConnectPlus' | 'ProfileImageFrame' | 'AnnualReport' | 'SocialImprovements' | 'AdvancedReporting'} FeatureCode
 */

/**
 * @typedef {import('utils/gcPlusSubscription/gcPlusSubscription').CustomerSubscriptionResponse} CustomerSubscriptionResponse
 */

// Base TanStack types
/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').UndefinedSpecificInitialDataOptions<TData>} UndefinedSpecificInitialDataOptions
 */

/**
 * @template TData
 * @typedef {import('utils/services/queryUtil').DefinedSpecificInitialDataOptions<TData>} DefinedSpecificInitialDataOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseQueryResult<TData, TError>} UseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseQueryResult<TData, TError>} DefinedUseQueryResult
 */

/**
 * @typedef AvailableContainer
 * @property {boolean} isAvailable Is feature available
 */

/**
 * @typedef UseQueryFeatureAvailable
 * @type {(UseQueryResult<CustomerSubscriptionResponse> | DefinedUseQueryResult<CustomerSubscriptionResponse>) & AvailableContainer}
 */

/**
 * Check if user has GCPlus subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasGcPlusSubscription = (customerGuid, queryParams = { enabled: true }) => {
    const response = useUserSubscription(customerGuid, queryParams);

    const { isPending, data: subscription } = response;

    let isAvailable;
    if (!isPending && subscription) {
        isAvailable = subscription[customerSubscriptions].some(({ groupCode }) => {
            return groupCode.indexOf(ConnectPlus) !== -1;
        });
    }
    return {
        ...response,
        isAvailable
    };
};

/**
 * Check if user has Live Session subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumLiveSessionView = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, LiveSessionView, queryParams);
};

/**
 * Check if user has Messaging subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumMessaging = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, Messaging, queryParams);
};

/**
 * Check if user has Live Track subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumLiveTrack = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, LiveTrack, queryParams);
};

/**
 * Check if user has Trails subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumTrails = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, Trails, queryParams);
};

/**
 * Check if user has Connect Plus subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumConnectPlus = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, ConnectPlus, queryParams);
};

/**
 * Check if user has Profile Image Frame subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumProfileImageFrame = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, ProfileImageFrame, queryParams);
};

/**
 * Check if user has Annual Report subcription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumAnnualReport = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, AnnualReport, queryParams);
};

/**
 * Check if user has Social Improvements subscription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasPremiumSocialImprovements = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, SocialImprovements, queryParams);
};

/**
 * Check if user has Social Improvements subscription
 *
 * @param {string} customerGuid Customer Guid
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
export const useUserHasAdvancedReporting = (customerGuid, queryParams = { enabled: true }) => {
    return useUserHasPremiumFeature(customerGuid, AdvancedReporting, queryParams);
};

/**
 * Check if user has premium subscription
 *
 * @param {string} customerGuid Customer Guid
 * @param {string} featureCode Feature code
 * @param {UndefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> | DefinedSpecificInitialDataOptions<CustomerSubscriptionResponse> =} queryParams Optional query config. Same as UseQueryOptions, but with queryKey and queryFn requirements removed
 * @return {UseQueryFeatureAvailable}
 */
const useUserHasPremiumFeature = (customerGuid, featureCode, queryParams = { enabled: true }) => {
    const response = useUserSubscription(customerGuid, queryParams);

    const { isPending, data: subscription } = response;

    let isAvailable;

    if (!isPending && subscription) {
        isAvailable = subscription[customerSubscriptions].some((subscription) => {
            return subscription[premiumFeatures]?.some(({ code }) => code === featureCode);
        });
    }
    return {
        ...response,
        isAvailable: !!isAvailable
    };
};

/**
 * Check if user has premium subscription using the provided API response data.
 *
 * @param {CustomerSubscriptionResponse} UserSubscriptionsData
 * @param {string} featureCode Feature code
 * @returns {boolean}
 */
export const userHasPremiumFeature = (UserSubscriptionsData, featureCode) => {
    return !!(
        UserSubscriptionsData &&
        UserSubscriptionsData[customerSubscriptions].some((subscription) =>
            subscription[premiumFeatures]?.some(({ code }) => code === featureCode)
        )
    );
};
